import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignIn as ClerkSignIn, SignUp as ClerkSignUp, useAuth, useClerk } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { checkPortalRole, clearPortalRoleCache, consumePortalDenied, markPortalDenied } from '../services/portalRole';
import Logo2 from '../assets/Logo2.png';

const STUDENT_URL = import.meta.env.VITE_STUDENT_URL || '';
const INVITE_TOKEN_KEY = 'hypebook.schoolAdmin.inviteToken';
const INVITE_EMAIL_KEY = 'hypebook.schoolAdmin.inviteEmail';
const INVITE_SCHOOL_KEY = 'hypebook.schoolAdmin.inviteSchool';
const INVITE_NAME_KEY = 'hypebook.schoolAdmin.inviteName';
const INVITE_HAS_ACCOUNT_KEY = 'hypebook.schoolAdmin.inviteHasAccount';

/**
 * Sign-in surface for School-Admin. Renders Clerk's in-app `<SignIn />`
 * with the sign-up surface still available — school admins typically
 * arrive here from an invite link and need to create an account on first
 * landing.
 *
 * Once the user is signed in, we hit `/v1/students/profile/me` to confirm
 * their server-side role is SCHOOL_ADMIN. Non-admins (a student who
 * somehow ended up on this URL) get signed out and bounced to the Student
 * app's sign-in page — same UX as the previous Logto callback flow.
 *
 * Invite-token continuation: if the user arrived via /invite/:token and
 * had to detour through sign-in, that route persists the token in
 * localStorage. This block is a fast path when the user lands back on
 * /signin; the app-wide InviteContinuation guard (App.tsx) is the
 * backstop that resumes the invite no matter where Clerk redirects.
 */
export default function SignIn() {
  const { isSignedIn, isLoaded, getToken, userId } = useAuth();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handled = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Set when a signed-in account without the SCHOOL_ADMIN role was bounced
  // back here (the wrong-portal branch below, or ProtectedRoute on a deep
  // link) — explains the "kicked to sign-in" behavior instead of leaving the
  // user guessing. Checked in BOTH channels: the ?denied=1 query (direct
  // links) and the sessionStorage flag (survives Clerk's signOut redirect,
  // which drops the query on dev instances). Read-and-cleared once via a
  // lazy initializer so re-renders don't lose the banner.
  const [deniedAccess] = useState(
    () => (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('denied') === '1') || consumePortalDenied(),
  );

  useEffect(() => {
    if (!isLoaded || !isSignedIn || handled.current) return;
    handled.current = true;

    void (async () => {
      try {
        // Invite continuation takes priority — the user came here to
        // accept an invite, not browse the panel. The invite page handles
        // the accept call once it remounts with the token in the URL.
        const inviteToken = localStorage.getItem(INVITE_TOKEN_KEY);
        if (inviteToken) {
          localStorage.removeItem(INVITE_TOKEN_KEY);
          navigate(`/invite/${inviteToken}`, { replace: true });
          return;
        }

        // Shared wrong-portal check (portalRole.ts) — the same one
        // ProtectedRoute runs for deep links; the shared cache means the
        // profile fetch happens once per session. It uses Clerk's getToken
        // directly rather than the module-level apiFetch wrapper: apiFetch
        // only attaches a Bearer token once AuthTokenSetter's effect
        // (App.tsx) has wired it up, and that sibling effect can commit
        // after this one.
        const verdict = userId ? await checkPortalRole(userId, getToken) : 'unknown';
        if (verdict === 'denied') {
          // Wrong portal. Sign out and return to OUR sign-in with a flag so
          // we can explain why. Previously this bounced silently to the
          // student app, which reads as a broken login to someone testing
          // the wrong account ("kicked back to sign-in with no message").
          // Clear the cached verdict so a since-fixed role re-checks on the
          // next attempt instead of replaying a stale denial. The denied flag
          // rides sessionStorage because Clerk's dev-instance signOut
          // redirect drops the ?denied=1 query.
          clearPortalRoleCache();
          markPortalDenied();
          await signOut({ redirectUrl: '/signin?denied=1' });
          return;
        }
        // 'ok' — confirmed school admin. 'unknown' — couldn't tell (network
        // error, or role missing because JIT provisioning is about to run on
        // the next API call); proceed and let the server guards enforce.
        navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('[SignIn] role check failed:', err);
        setErrorMessage(err instanceof Error ? err.message : t('auth.signInRedirectFailed'));
        handled.current = false;
      }
    })();
  }, [isLoaded, isSignedIn, navigate, signOut, t, getToken, userId]);

  // Show a neutral spinner while Clerk is still booting OR once signed in
  // and confirming role. Critically, we do NOT render <ClerkSignIn> until
  // Clerk is loaded — otherwise a returning admin who already has a session
  // sees the sign-in form flash for the duration of the Clerk boot before
  // it's swapped for the redirect, which is the visible login flicker.
  if (!isLoaded || isSignedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-gray-600">
            {isSignedIn ? t('auth.completingSignIn') : t('auth.validatingSession')}
          </p>
          {errorMessage ? <p className="mt-2 text-red-600 text-sm">{errorMessage}</p> : null}
        </div>
      </div>
    );
  }

  // Post-auth destination. When the user detoured here from an invite link,
  // Clerk MUST land them back on /invite/:token so the accept call runs and
  // elevates them to SCHOOL_ADMIN. The post-sign-in effect above also tries
  // this, but it's racy: ClerkProvider's signInFallbackRedirectUrl
  // (/dashboard) fires on auth completion and was winning — dropping
  // the invitee into the panel as a STUDENT, which instantly 403s and signs
  // them out with the invite never accepted. A force redirect derived from
  // the stashed token removes the race deterministically.
  const pendingInviteToken = typeof window !== 'undefined' ? localStorage.getItem(INVITE_TOKEN_KEY) : null;
  const postAuthRedirectUrl = pendingInviteToken ? `/invite/${pendingInviteToken}` : '/dashboard';
  // Invited admins detour here from /invite/:token, which stashes the email
  // they were invited with. Prefill it so they just set a password — and the
  // accept step (which matches the signed-in email to the invite) lines up.
  const pendingInviteEmail = typeof window !== 'undefined' ? localStorage.getItem(INVITE_EMAIL_KEY) : null;

  const isInviteFlow = !!pendingInviteToken;
  // True when a Hypebook account already exists for the invited email (an
  // existing user, or a re-invite of someone who already accepted). Stashed by
  // InviteAccept from the invite preview's `hasAccount`. Drives the surface:
  //   invite + no account  → SIGN-UP  (the common brand-new-teacher case)
  //   invite + has account → SIGN-IN  (else "create account" dead-ends at
  //                                    Clerk's "email is taken")
  //   no invite            → SIGN-IN  (returning admin)
  const pendingInviteHasAccount = typeof window !== 'undefined' && localStorage.getItem(INVITE_HAS_ACCOUNT_KEY) === '1';
  const showInviteSignUp = isInviteFlow && !pendingInviteHasAccount;
  // School the invitee is joining (stashed by InviteAccept) — shown above the
  // form so they have context, not just a bare prefilled email.
  const pendingInviteSchool = typeof window !== 'undefined' ? localStorage.getItem(INVITE_SCHOOL_KEY) : null;

  // routing="virtual" — keeps the whole flow at /signin without URL navigation
  // between steps (path routing re-fired prepareFirstFactor → a second OTP).
  // appearance hides the social/OAuth buttons + the "or" divider so the form
  // stays a clean email + password. initialValues prefills the invited email.
  const sharedAppearance = {
    elements: {
      socialButtonsRoot: { display: 'none' },
      dividerRow: { display: 'none' },
      // Hide the Clerk card's built-in logo — it renders the Clerk instance's
      // (stale, cloned-from-dev) logo, and this page already shows the current
      // wordmark (Logo2) above the card. Prevents old + new logos together.
      logoBox: { display: 'none' },
    },
  } as const;
  const prefill = pendingInviteEmail ? { emailAddress: pendingInviteEmail } : undefined;
  // The inviting admin already entered the invitee's name — prefill it into the
  // sign-up form so they don't retype it. Clerk's SignUp initialValues takes
  // first/last separately, so split the single stored name on whitespace
  // (first token = first name, the rest = last name). Only the SIGN-UP surface
  // collects a name, so this richer prefill is kept off ClerkSignIn (whose
  // initialValues type doesn't accept first/last).
  const pendingInviteName = typeof window !== 'undefined' ? localStorage.getItem(INVITE_NAME_KEY) : null;
  const nameParts = (pendingInviteName ?? '').trim().split(/\s+/).filter(Boolean);
  const signUpPrefill =
    pendingInviteEmail || nameParts.length > 0
      ? {
          ...(pendingInviteEmail ? { emailAddress: pendingInviteEmail } : {}),
          ...(nameParts[0] ? { firstName: nameParts[0] } : {}),
          ...(nameParts.length > 1 ? { lastName: nameParts.slice(1).join(' ') } : {}),
        }
      : undefined;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 py-12 gap-6">
      <img className="w-52 h-10" src={Logo2} alt="School Admin" />
      {/* Suppress the denied banner during an invite flow. The flag is set when
          some OTHER account was bounced from this portal (e.g. a platform
          admin), and it survives in sessionStorage until consumed — so an
          invitee who opens their link in the same tab would see a scary "that
          account doesn't have access" error stacked on top of a perfectly
          valid "create your account" form. The invite is a fresh start; the
          stale denial is not about the account they're about to create. */}
      {deniedAccess && !isInviteFlow ? (
        <div className="w-full max-w-md rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-700">
          {t('auth.noAccess', {
            defaultValue: "That account doesn't have access to School Admin. Sign in with a school-admin account.",
          })}
          {STUDENT_URL ? (
            <>
              {' '}
              <a href={`${STUDENT_URL}/sign-in`} className="font-medium underline">
                {t('auth.openStudentApp', { defaultValue: 'Open the student app' })}
              </a>
            </>
          ) : null}
        </div>
      ) : null}
      {isInviteFlow && (
        <div className="text-center max-w-sm">
          <h1 className="text-xl font-semibold text-gray-900">
            {showInviteSignUp
              ? pendingInviteSchool
                ? t('auth.inviteHeading', { defaultValue: 'Create your account to manage {{school}}', school: pendingInviteSchool })
                : t('auth.inviteHeadingGeneric', { defaultValue: 'Create your account to accept your invite' })
              : pendingInviteSchool
                ? t('auth.inviteSignInHeading', { defaultValue: 'Sign in to manage {{school}}', school: pendingInviteSchool })
                : t('auth.inviteSignInHeadingGeneric', { defaultValue: 'Sign in to accept your invite' })}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {showInviteSignUp
              ? t('auth.inviteSubheading', { defaultValue: 'Set a password to finish joining your school team.' })
              : t('auth.inviteSignInSubheading', { defaultValue: 'You already have a Hypebook account — sign in to finish joining your school team.' })}
          </p>
        </div>
      )}
      {showInviteSignUp ? (
        <ClerkSignUp
          routing="virtual"
          forceRedirectUrl={postAuthRedirectUrl}
          signInForceRedirectUrl={postAuthRedirectUrl}
          initialValues={signUpPrefill}
          appearance={sharedAppearance}
        />
      ) : (
        <ClerkSignIn
          routing="virtual"
          forceRedirectUrl={postAuthRedirectUrl}
          signUpForceRedirectUrl={postAuthRedirectUrl}
          initialValues={prefill}
          appearance={sharedAppearance}
        />
      )}
    </div>
  );
}
